'use memo'

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { REGISTRATION_ROLE_PARAM } from '@sportninja/common/features/registration/registrationUrlParams'
import {
  lockedRoleFromParam,
  isLockedRoleAvailable,
  shouldApplyLinkedRole,
  type LockedRole,
} from '../utils/lockedRole'

interface RegistrationType {
  id?: string
}

interface Params<T extends RegistrationType> {
  /** The role types from the registration context, once they have loaded. */
  playerType: T | null | undefined
  teamOfficialType: T | null | undefined
  /** The division's current offer — the link is ignored if it cannot be met. */
  playerRegistrationEnabled?: boolean
  teamRegistrationEnabled?: boolean
  teamRegistrationFull?: boolean
  /** The role currently chosen, from context. */
  role: RegistrationType | null | undefined
  setRole: (type: T) => void
  /** Cleared when a conflicting role is corrected — the chosen player belongs
   * to the role that was abandoned. */
  setSelectedPlayer: (player: null) => void
}

interface Result {
  /** The role the link names, or null when it names none this step can offer. */
  lockedRole: LockedRole | null
  /**
   * Whether the opposite card should be disabled.
   *
   * Only the retired `New_SelectYourRole` screen acts on these — see the note
   * on the hook.
   */
  isPlayerLockedOut: boolean
  isTeamLockedOut: boolean
}

/**
 * Pre-select the role a link names (`?type=player` / `?type=team`).
 *
 * The sibling of `usePreselectedDivision` and `usePreselectedStartTeam`: the
 * link fills the answer in, and the reader can still choose the other side.
 *
 * It CORRECTS rather than only filling an empty field, which the other two do
 * not: a resumed session restores its own `registration_type_id`, and that can
 * disagree with the link the reader just followed. The link is the fresher
 * choice, so it wins — and the player chosen under the abandoned role goes with
 * it, since they belong to a role no longer selected.
 *
 * That correction is owed ONCE, on arrival. It used to be re-decided on every
 * change of `role?.id`, which made the role unchangeable while `?type=` sat in
 * the URL: pressing the other card looked exactly like a stale session, so the
 * effect immediately reverted it and cleared the chosen player as it went. See
 * `shouldApplyLinkedRole`, which draws that line.
 *
 * The `isPlayerLockedOut` / `isTeamLockedOut` flags it returns are used ONLY by
 * the retired `New_SelectYourRole` screen, which disables the other card. The
 * merged start step discards them: there, a link highlights an answer without
 * taking one away.
 */
export const usePreselectedRole = <T extends RegistrationType>({
  playerType,
  teamOfficialType,
  playerRegistrationEnabled,
  teamRegistrationEnabled,
  teamRegistrationFull,
  role,
  setRole,
  setSelectedPlayer,
}: Params<T>): Result => {
  const { search } = useLocation()

  const requested = lockedRoleFromParam(
    new URLSearchParams(search).get(REGISTRATION_ROLE_PARAM)
  )
  const lockedRole = isLockedRoleAvailable(requested, {
    playerRegistrationEnabled,
    teamRegistrationEnabled,
    teamRegistrationFull,
  })
    ? requested
    : null

  const lockedType = lockedRole === 'player' ? playerType : teamOfficialType
  // Depend on the ID, not the object. RegistrationContext's `playerType` /
  // `teamOfficialType` fall back to a fresh `{}` until the types load, so the
  // object identity changes every render while the id stays undefined — enough
  // to re-run the effect below forever.
  const lockedTypeId = lockedType?.id

  // Which link has already been honoured. A ref rather than state: it must not
  // itself cause a render, and it is read inside the effect it guards.
  //
  // Keyed by the role the link names, so a reader who edits `?type=` in place —
  // or follows a second link into the same mounted screen — gets that new link
  // applied, while the one already spent stays spent.
  const appliedRole = useRef<LockedRole | null>(null)

  useEffect(() => {
    if (
      !shouldApplyLinkedRole({
        linkedRole: lockedRole,
        linkedTypeId: lockedTypeId,
        currentRoleId: role?.id,
        alreadyApplied: appliedRole.current === lockedRole,
      })
    ) {
      return
    }
    // Marked before setting, so the state change this triggers cannot re-enter
    // and re-apply the same link.
    appliedRole.current = lockedRole
    setRole(lockedType as T)
    // Only a genuine conflict discards a selection; arriving with no role at
    // all is the ordinary first-visit case and has nothing to clear.
    if (role?.id) {
      setSelectedPlayer(null)
    }
    // Only IDs and the setter-free values are dependencies. `setRole` /
    // `setSelectedPlayer` come from RegistrationContext, which defines its
    // setters inline, so both are NEW functions every render; `lockedType` is a
    // fresh `{}` until the types load. Listing any of them re-runs this effect
    // on every render, and it sets state — an update loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedRole, lockedTypeId, role?.id])

  return {
    lockedRole,
    isPlayerLockedOut: lockedRole === 'team',
    isTeamLockedOut: lockedRole === 'player',
  }
}
